/**
 * MainChart — renders the Plotly figure built by lib/chartFigure.ts.
 * Pulls bars from /api/ohlcv via React Query (60s stale), rebuilds the
 * figure whenever bars / overlays / panels / focus change.
 */
import { useMemo, useCallback, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LazyPlot as Plot } from '@/components/LazyPlot';
import { useStore, readThemeColors, type ChartDrawing } from '@/store';
import { api, isSynthetic } from '@/lib/api';
import { fetchBars } from '@/lib/bars';
import { useLiveCandle } from '@/hooks/useLiveCandle';
import { buildChartFigure, type OptionWall } from '@/lib/chartFigure';
import type { Bar, SubpanelId } from '@/types';

interface OptionContract {
  strike:       number;
  type:         'call' | 'put';
  openInterest: number;
}

// Module-level stable reference so `drawingsMap[focus] ?? EMPTY_DRAWINGS`
// returns the SAME array identity for any drawing-free symbol -- otherwise a
// fresh [] each render would invalidate the merge memo on every tick.
const EMPTY_DRAWINGS: ChartDrawing[] = [];

// Shared style for the small drawing-toolbar buttons.
const toolBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid #333', borderRadius: 3,
  color: '#bbb', fontFamily: 'Consolas, monospace', fontSize: 10,
  padding: '2px 6px', cursor: 'pointer', lineHeight: 1.4,
};

export function MainChart() {
  const focus      = useStore(s => s.focusSymbol);
  const period     = useStore(s => s.period);
  const interval   = useStore(s => s.interval);
  const overlays   = useStore(s => s.overlays);
  const panels     = useStore(s => s.panels);
  const entry      = useStore(s => s.entryPrice);
  const tp         = useStore(s => s.tpPrice);
  const sl         = useStore(s => s.slPrice);
  const theme         = useStore(s => s.theme);          // figure rebuilds on theme swap
  const vpSessions    = useStore(s => s.vpSessions);     // and on VP session count change
  const subHeights    = useStore(s => s.subpanelHeights);
  const setSubHeight  = useStore(s => s.setSubpanelHeight);
  const setEntry      = useStore(s => s.setEntry);
  const setTp         = useStore(s => s.setTp);
  const setSl         = useStore(s => s.setSl);
  // Chart drawings (persisted per symbol) + named templates.
  const drawingsMap        = useStore(s => s.drawings);
  const templatesMap       = useStore(s => s.drawingTemplates);
  const setDrawings        = useStore(s => s.setDrawings);
  const clearDrawings      = useStore(s => s.clearDrawings);
  const saveTemplate       = useStore(s => s.saveDrawingTemplate);
  const applyTemplate      = useStore(s => s.applyDrawingTemplate);
  const deleteTemplate     = useStore(s => s.deleteDrawingTemplate);
  const wrapperRef    = useRef<HTMLDivElement | null>(null);
  const baseShapeCountRef = useRef(0);   // where base shapes end / user drawings begin
  const [tplName, setTplName] = useState('');
  const [showSave, setShowSave] = useState(false);

  // Feed live quote ticks into the current candle between the 15s poll cycles
  // (intraday only; the poll stays authoritative). Makes the chart truly live.
  useLiveCandle(focus, period, interval);

  // Shared fetcher (lib/bars) -- ChartHeader reads this SAME key, and React
  // Query keeps one queryFn per key. Both must use fetchBars or the raw one
  // can win and hand this component un-normalized bars.
  const { data: bars, isLoading, isError } = useQuery<Bar[]>({
    queryKey: ['bars', focus, period, interval],
    queryFn: () => fetchBars(focus, period, interval),
    staleTime:       15_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  // Options walls: only fetched when the `owalls` overlay is toggled on,
  // so the chart's primary load stays light.
  const wallsEnabled = overlays.includes('owalls');
  const { data: chain } = useQuery<OptionContract[]>({
    queryKey: ['options-walls', focus],
    queryFn:  () => api(`/api/options/${encodeURIComponent(focus)}`),
    enabled:  wallsEnabled,
    staleTime: 5 * 60_000,
  });

  const walls = useMemo<OptionWall[]>(() => {
    if (!wallsEnabled || !chain) return [];
    // Aggregate OI per (strike, side); keep top 3 calls + top 3 puts.
    const agg: Record<string, { strike: number; side: 'call' | 'put'; oi: number }> = {};
    for (const c of chain) {
      const key = `${c.strike}_${c.type}`;
      if (!agg[key]) agg[key] = { strike: c.strike, side: c.type, oi: 0 };
      agg[key].oi += c.openInterest;
    }
    const ranked = Object.values(agg).sort((a, b) => b.oi - a.oi);
    const calls = ranked.filter(r => r.side === 'call').slice(0, 3);
    const puts  = ranked.filter(r => r.side === 'put') .slice(0, 3);
    return [...calls, ...puts];
  }, [chain, wallsEnabled]);

  const figure = useMemo(() => {
    if (!bars || bars.length === 0) return null;
    return buildChartFigure({
      symbol: focus, bars, interval, overlays, panels, walls,
      lines:      { entry, tp, sl },
      vpSessions,
      subHeights,
    });
  }, [bars, focus, interval, overlays, panels, walls, entry, tp, sl, theme, vpSessions, subHeights]);

  // User drawings for the CURRENT symbol. Stable empty array so an unchanged
  // (drawing-free) symbol doesn't churn the merge memo every render.
  const myDrawings = drawingsMap[focus] ?? EMPTY_DRAWINGS;
  const templateNames = useMemo(() => Object.keys(templatesMap).sort(), [templatesMap]);

  // Merge the base figure shapes with the user's persisted drawings. This is
  // what makes drawing WORK: buildChartFigure regenerates the figure from
  // scratch on every 15s bar refresh / overlay toggle / theme swap, so without
  // re-appending the stored drawings here they would vanish seconds after being
  // drawn. baseShapeCountRef records the split so onRelayout can tell a dragged
  // TP/SL line (index < base) from a dragged user drawing (index >= base).
  const plotFigure = useMemo(() => {
    if (!figure) return null;
    const layout = figure.layout as { shapes?: unknown[] } & Record<string, unknown>;
    const base = Array.isArray(layout.shapes) ? layout.shapes : [];
    baseShapeCountRef.current = base.length;
    const accent = readThemeColors().accent;
    return {
      ...figure,
      layout: {
        ...layout,
        shapes: [...base, ...myDrawings],
        // Peak drawing feel: new shapes use the theme accent, and a drawn
        // shape stays selectable (so Plotly's eraseshape can delete just it).
        newshape: { line: { color: accent, width: 2 }, opacity: 0.95 },
        activeshape: { fillcolor: accent, opacity: 0.12 },
      },
    };
  }, [figure, myDrawings]);

  // Build a list of (panelId, divider y-position-as-fraction-from-top)
  // for each active subpanel. Each divider sits at the TOP of its panel
  // in y-domain coordinates; we convert to (1 - y) for the screen-top
  // CSS percentage. Stacking math mirrors chartFigure.ts so the
  // dividers line up exactly with the Plotly subpanel boundaries.
  const subBottomMargin = 0.04;
  const subGap          = 0.02;
  const dividers = useMemo(() => {
    if (panels.length === 0) return [] as Array<{ id: SubpanelId; topPct: number }>;
    const heights = panels.map(p => Math.max(0.04, Math.min(0.25, subHeights[p] ?? 0.10)));
    const totalSubH = heights.reduce((a, b) => a + b, 0);
    const mainBot   = subBottomMargin + totalSubH + subGap;
    const out: Array<{ id: SubpanelId; topPct: number }> = [];
    let cursorY = mainBot - subGap;          // top of first subpanel
    panels.forEach((p, i) => {
      out.push({ id: p, topPct: (1 - cursorY) * 100 });
      cursorY -= heights[i];
    });
    return out;
  }, [panels, subHeights]);

  // Per-divider drag. Each updates only the subpanel below it; the
  // main chart absorbs the net change in subpanel area.
  const onDividerMouseDown = useCallback((panelId: SubpanelId) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const wrap = wrapperRef.current;
    if (!wrap) return;
    const startY     = e.clientY;
    const startH     = subHeights[panelId] ?? 0.10;
    const totalPx    = wrap.getBoundingClientRect().height;

    const onMove = (mv: MouseEvent) => {
      const dy = mv.clientY - startY;
      // Drag UP (negative dy) → this panel grows.
      // 1 px of mouse movement = 1/totalPx of y-domain change.
      const deltaH = -(dy / totalPx);
      setSubHeight(panelId, startH + deltaH);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [subHeights, setSubHeight]);

  // ── Drag handler ─────────────────────────────────────────────────────
  // Plotly emits relayout events keyed like "shapes[N].y0" / "shapes[N].y1"
  // when an editable shape is dragged. We map the index back to the right
  // field via figure.lineShapeIndex and push the new price into the store,
  // which re-renders OrderTicket simultaneously (two-way binding).
  const onRelayout = useCallback((ev: Readonly<Record<string, unknown>>) => {
    if (!figure) return;
    const base = baseShapeCountRef.current;

    // (1) A draw or an erase emits the WHOLE shapes array. That is authoritative
    // for the user's drawing set, so persist everything past the base shapes
    // (the TP/SL/overlay lines the figure owns) and stop -- it supersedes any
    // per-index deltas Plotly may include in the same event.
    const full = (ev as Record<string, unknown>).shapes;
    if (Array.isArray(full)) {
      setDrawings(focus, full.slice(base) as ChartDrawing[]);
      return;
    }

    // (2) A drag emits per-index coordinate keys. Route tracked-line drags to
    // the price setters (two-way bind with OrderTicket) and user-drawing drags
    // to the persisted store so the moved shape survives the next rebuild.
    const idx = figure.lineShapeIndex;
    const setters: Array<((v: number) => void) | null> = [];
    if (idx.entry != null) setters[idx.entry] = (v) => setEntry(v);
    if (idx.tp    != null) setters[idx.tp]    = (v) => setTp(v);
    if (idx.sl    != null) setters[idx.sl]    = (v) => setSl(v);

    const edits: Record<number, Record<string, unknown>> = {};
    let sawUserEdit = false;
    for (const [key, val] of Object.entries(ev)) {
      const m = /^shapes\[(\d+)\]\.(x0|x1|y0|y1|path)$/.exec(key);
      if (!m) continue;
      const shapeIdx = parseInt(m[1], 10);
      const field = m[2];
      if (shapeIdx < base) {
        // Tracked price line: only y matters, snapped to 2dp.
        const set = setters[shapeIdx];
        if (set && (field === 'y0' || field === 'y1') && typeof val === 'number' && Number.isFinite(val)) {
          set(Number(val.toFixed(2)));
        }
      } else {
        (edits[shapeIdx - base] ??= {})[field] = val;
        sawUserEdit = true;
      }
    }
    if (sawUserEdit) {
      setDrawings(focus, myDrawings.map((d, i) => edits[i] ? { ...d, ...edits[i] } : d));
    }
  }, [figure, focus, myDrawings, setDrawings, setEntry, setTp, setSl]);

  if (isLoading) return <ChartShell message="LOADING BARS..." />;
  // A fetch FAILURE must say so -- not assert the fact "NO DATA". Distinguish a
  // load error (retryable) from a symbol/timeframe that genuinely has no bars.
  if (isError) return <ChartShell message="FAILED TO LOAD - RETRYING..." />;
  if (!bars || bars.length === 0) return <ChartShell message="NO DATA FOR THIS TIMEFRAME" />;
  if (!figure || !plotFigure) return <ChartShell message="..." />;

  // Badge the chart when the server returned fabricated demo bars, matching the
  // News/Options tabs -- otherwise invented candles + indicators read as real
  // market data (the isSynthetic infra existed but the chart never used it).
  const demo = isSynthetic('/api/ohlcv/' + encodeURIComponent(focus));

  return (
    <div
      ref={wrapperRef}
      style={{ height: 'calc(100% - 72px)', width: '100%', minHeight: 0, position: 'relative' }}
    >
      {demo && (
        <div style={{
          position: 'absolute', top: 8, right: 8, zIndex: 6,
          background: '#3a2a00', color: '#f5a623',
          fontSize: 10, letterSpacing: 1.5, padding: '3px 8px',
          borderRadius: 4, border: '1px solid #5a4200',
          fontFamily: 'Consolas, monospace', pointerEvents: 'none',
        }}>DEMO DATA</div>
      )}
      {/* ── Drawing toolbar ──────────────────────────────────────────────
          Appears once the user has drawn something or saved a template.
          Draw itself is the Plotly modebar (line / path / erase); this bar
          adds bulk delete + named templates on top of the per-shape erase.
          Drawings persist per symbol and survive rebuilds/reloads. */}
      {(myDrawings.length > 0 || templateNames.length > 0) && (
        <div style={{
          position: 'absolute', top: 8, left: 8, zIndex: 6,
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', maxWidth: 'calc(100% - 120px)',
          background: 'rgba(10,10,10,0.82)', border: '1px solid #222', borderRadius: 5,
          padding: '4px 7px', fontFamily: 'Consolas, monospace', fontSize: 10, color: '#9a9a9a',
        }}>
          <span style={{ color: myDrawings.length ? 'var(--accent)' : '#666', letterSpacing: 0.5 }}>
            {myDrawings.length} drawing{myDrawings.length === 1 ? '' : 's'}
          </span>
          {myDrawings.length > 0 && (
            <button onClick={() => clearDrawings(focus)} title="Delete all drawings on this symbol" style={toolBtn}>
              Clear all
            </button>
          )}
          {myDrawings.length > 0 && (
            <button onClick={() => setShowSave(v => !v)} title="Save these drawings as a reusable template" style={toolBtn}>
              Save template
            </button>
          )}
          {showSave && myDrawings.length > 0 && (
            <span style={{ display: 'inline-flex', gap: 3 }}>
              <input
                value={tplName}
                onChange={e => setTplName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && tplName.trim()) { saveTemplate(tplName.trim(), focus); setTplName(''); setShowSave(false); } }}
                placeholder="template name"
                autoFocus
                style={{ background: '#000', border: '1px solid #333', color: '#ddd', fontFamily: 'inherit', fontSize: 10, padding: '2px 5px', width: 110, outline: 'none' }}
              />
              <button
                onClick={() => { if (tplName.trim()) { saveTemplate(tplName.trim(), focus); setTplName(''); setShowSave(false); } }}
                disabled={!tplName.trim()}
                style={{ ...toolBtn, opacity: tplName.trim() ? 1 : 0.4 }}
              >Save</button>
            </span>
          )}
          {templateNames.length > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, borderLeft: '1px solid #2a2a2a', paddingLeft: 6 }}>
              <span style={{ color: '#666' }}>tpl:</span>
              {templateNames.map(n => (
                <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, border: '1px solid #333', borderRadius: 3, padding: '0 2px 0 4px' }}>
                  <button onClick={() => applyTemplate(n, focus)} title={`Apply "${n}" to ${focus}`} style={{ ...toolBtn, border: 'none', padding: '2px 2px' }}>{n}</button>
                  <button onClick={() => deleteTemplate(n)} title={`Delete template "${n}"`} style={{ ...toolBtn, border: 'none', padding: '2px 4px', color: '#c46b6b' }}>&times;</button>
                </span>
              ))}
            </span>
          )}
        </div>
      )}
      {/* One draggable divider above each active subpanel. Each one
          controls the height of the subpanel directly below it; the
          main chart absorbs the net change in subpanel area. */}
      {dividers.map(({ id, topPct }) => (
        <div
          key={id}
          onMouseDown={onDividerMouseDown(id)}
          title={`Drag to resize ${id.toUpperCase()}`}
          style={{
            position: 'absolute',
            top:      `calc(${topPct}% - 3px)`,
            left:     0,
            right:    0,
            height:   6,
            cursor:   'row-resize',
            zIndex:   5,
          }}
        >
          <div style={{
            height: '100%', width: '100%',
            background: 'transparent',
            borderTop:    '1px solid #1a1a1a',
            borderBottom: '1px solid #1a1a1a',
            transition: 'background 120ms ease',
          }}
          onMouseEnter={(e) => { (e.currentTarget.style.background = 'var(--accent-soft)'); }}
          onMouseLeave={(e) => { (e.currentTarget.style.background = 'transparent'); }}
          />
        </div>
      ))}
      <Plot
        data={plotFigure.data as unknown as Plotly.Data[]}
        layout={{ ...(plotFigure.layout as unknown as Partial<Plotly.Layout>), autosize: true }}
        config={{
          // Hide the mode bar unless the user hovers the chart. Keeps the
          // trader focused on candles + overlays; the toolbar reappears
          // instantly on mouseover for zoom/pan/draw actions.
          displayModeBar: 'hover',
          scrollZoom:     true,
          responsive:     true,
          // Enable shape position drag for TP/SL lines, but scope the
          // `edits` flags so Plotly doesn't render "Click to enter X
          // axis title" ghosts or let users rename traces / titles.
          editable:       true,
          edits: {
            shapePosition:     true,
            annotationPosition:false,
            annotationTail:    false,
            annotationText:    false,
            axisTitleText:     false,
            colorbarPosition:  false,
            colorbarTitleText: false,
            legendPosition:    false,
            legendText:        false,
            titleText:         false,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          modeBarButtonsToAdd: ['drawline', 'drawopenpath', 'eraseshape'] as any,
          // Drop the noisy defaults that duplicate scrollZoom or leak
          // trace data. Keeps: zoom, pan, autoscale, camera, draw tools.
          modeBarButtonsToRemove: [
            'sendDataToCloud', 'toggleSpikelines',
            'hoverClosestCartesian', 'hoverCompareCartesian',
            'lasso2d', 'select2d',
          ],
          // Never suggest the "Made with Plotly" hyperlink.
          displaylogo: false,
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
        onRelayout={onRelayout as unknown as (e: Readonly<Plotly.PlotRelayoutEvent>) => void}
      />
    </div>
  );
}

function ChartShell({ message }: { message: string }) {
  return (
    <div style={{
      height: 'calc(100% - 72px)', width: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#333', fontSize: 12, letterSpacing: 2,
      fontFamily: "Consolas, monospace",
    }}>
      {message}
    </div>
  );
}
